import React, { useState, useEffect } from 'react';
import axiosInstance from "../../../interceptor/axiosInstance";
import NotificationService from "../../Utils/NotificationService";

const WixGuide = () => {
  const [orgCode, setOrgCode] = useState('');
  const [copiedSpecific, setCopiedSpecific] = useState(false);
  const [copiedInlineAll, setCopiedInlineAll] = useState(false);
  const [copiedFloatingAll, setCopiedFloatingAll] = useState(false);

  useEffect(() => {
    const fetchOrgDetails = async () => {
      const token = localStorage.getItem("authToken");
      if (!token) {
        setOrgCode("YOUR_ORG_CODE");
        return;
      }
      try {
        const response = await axiosInstance.get(`/org/org-details`);
        const data = response.data[0];
        setOrgCode(data.organisation_code);
      } catch (error) {
        console.error('Error fetching organization details:', error);
        setOrgCode("YOUR_ORG_CODE");
      }
    };
    fetchOrgDetails();
  }, []);

  const fullCode = `<div id="chat-bot-container"></div>\n<script src="https://ezzdesk.com/shared_prompt.js?code=${orgCode}"></script>`;

  const handleCopy = (text, setCopied) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 3000);
    }).catch((err) => {
      NotificationService.error('Failed to copy text: ' + err);
    });
  };

  const ImagePlaceholder = () => (
    <div style={{
      width: '100%',
      height: '300px',
      backgroundColor: '#f0f0f0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      border: '2px dashed #ccc',
      borderRadius: '8px',
      margin: '20px 0',
      color: '#999',
      fontSize: '18px'
    }}>
      Image Placeholder
    </div>
  );

  return (
    <div style={{
      padding: '40px',
      maxWidth: '800px',
      margin: '0 auto',
      fontFamily: 'Arial, sans-serif',
      background: '#ffffff',
      color: '#333',
      lineHeight: '1.6'
    }}>
      <h1 style={{ fontSize: '28px', marginBottom: '20px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
        Adding Ezzdesk Widget to Wix Website
      </h1>
      
      <p style={{ fontSize: '16px', marginBottom: '30px', color: '#555' }}>
        You've made the decision to enhance your Wix website by incorporating your Ezzdesk widget, but unsure of where to start? This step-by-step guide will navigate you through the whole installation process.
      </p>

      <div style={{ background: '#fff3cd', borderLeft: '4px solid #ffecb5', padding: '10px', marginBottom: '30px', fontSize: '14px' }}>
        <strong>Note:</strong> An <strong>inline widget</strong> is embedded at a specific spot on the page and disappears from view as users scroll through the content. A <strong>floating widget</strong> is anchored to a certain position on a page and stays there upon scrolling.
      </div>

      <div style={{ marginBottom: '50px' }}>
        <h2 style={{ fontSize: '24px', marginBottom: '20px', color: '#222' }}>Adding a widget to a specific page</h2>
        
        <p style={{ fontSize: '15px', color: '#666', marginBottom: '10px' }}>
          <strong>1. Go to Wix website editor</strong><br/>
          Log in to your Wix account and choose the website where you'd like to add the widget. Click <strong>Edit Site</strong>.
        </p>
        <img src="/assets/wix-1.png" alt="Paste Code" style={{ maxWidth: '100%', borderRadius: '8px', border: '1px solid #ddd', margin: '15px 0' }} />

        <p style={{ fontSize: '15px', color: '#666', marginBottom: '10px', marginTop: '20px' }}>
          <strong>2. Add HTML block</strong><br/>
          Click the <strong>Plus</strong> icon, go to the <strong>Embed Code</strong> section and select <strong>Embed HTML</strong> on the <strong>Popular Embeds</strong> tab:
        </p>
        <img src="/assets/wix-3.png" alt="Paste Code" style={{ maxWidth: '100%', borderRadius: '8px', border: '1px solid #ddd', margin: '15px 0' }} />


        <p style={{ fontSize: '15px', color: '#666', marginBottom: '10px', marginTop: '20px' }}>
          <strong>3. Paste the installation code</strong><br/>
          Click <strong>Enter Code</strong>, paste your installation code and press <strong>Update</strong>:
        </p>
        
        <div style={{ position: 'relative', marginBottom: '20px' }}>
          <pre style={{ background: '#f4f4f4', padding: '20px', border: '1px solid #ddd', borderRadius: '5px', fontFamily: 'Courier New, monospace', fontSize: '14px', overflowX: 'auto' }}>
            {fullCode}
          </pre>
          <button onClick={() => handleCopy(fullCode, setCopiedSpecific)} className='btn-color' style={{ position: 'absolute', top: '15px', right: '15px', padding: '6px 16px', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
            {copiedSpecific ? 'Copied!' : 'Copy'}
          </button>
        </div>
        <img src="/assets/wix-4.png" alt="Paste Code" style={{ maxWidth: '100%', borderRadius: '8px', border: '1px solid #ddd', margin: '15px 0' }} />


        <p style={{ fontSize: '15px', color: '#666', marginBottom: '10px', marginTop: '20px' }}>
          <strong>4. Adjust the widget's size and position</strong><br/>
          Drag the widget to the right page area and stretch it to get the size you need. Keep stretching until scroll bars are gone and all the required information fits into the block:
        </p>
        <img src="/assets/wix-2.gif" alt="Paste Code" style={{ maxWidth: '100%', borderRadius: '8px', border: '1px solid #ddd', margin: '15px 0' }} />


        <p style={{ fontSize: '15px', color: '#666', marginBottom: '10px', marginTop: '20px' }}>
          <strong>5. Adjust the mobile appearance and Publish</strong><br/>
          Switch to the mobile editor by clicking on the <strong>Mobile</strong> icon, adjust your widget's size by stretching it, and press <strong>Publish</strong>:
        </p>
        <img src="/assets/wix-5.png" alt="Paste Code" style={{ maxWidth: '100%', borderRadius: '8px', border: '1px solid #ddd', margin: '15px 0' }} />

      </div>

      <div style={{ marginBottom: '50px' }}>
        <h2 style={{ fontSize: '24px', marginBottom: '20px', color: '#222' }}>Adding an inline widget to all pages</h2>
        
        <p style={{ fontSize: '15px', color: '#666', marginBottom: '10px' }}>
          <strong>1. Go to Wix website editor</strong><br/>
          Log in to your Wix account and choose the website where you'd like to add the widget. Click <strong>Edit Site</strong>.
        </p>
        <img src="/assets/wix-6.png" alt="Paste Code" style={{ maxWidth: '100%', borderRadius: '8px', border: '1px solid #ddd', margin: '15px 0' }} />


        <p style={{ fontSize: '15px', color: '#666', marginBottom: '10px', marginTop: '20px' }}>
          <strong>2. Add HTML block</strong><br/>
          Click <strong>Plus</strong> icon, go to <strong>Embed</strong> section and select <strong>Embed a Widget</strong> on <strong>Custom Embeds</strong> tab:
        </p>
        <img src="/assets/wix-7.png" alt="Paste Code" style={{ maxWidth: '100%', borderRadius: '8px', border: '1px solid #ddd', margin: '15px 0' }} />


        <p style={{ fontSize: '15px', color: '#666', marginBottom: '10px', marginTop: '20px' }}>
          <strong>3. Paste the installation code</strong><br/>
          Click <strong>Enter Code</strong>, paste your installation code and press <strong>Update</strong>:
        </p>

        <div style={{ position: 'relative', marginBottom: '20px' }}>
          <pre style={{ background: '#f4f4f4', padding: '20px', border: '1px solid #ddd', borderRadius: '5px', fontFamily: 'Courier New, monospace', fontSize: '14px', overflowX: 'auto' }}>
            {fullCode}
          </pre>
          <button onClick={() => handleCopy(fullCode, setCopiedInlineAll)} className='btn-color' style={{ position: 'absolute', top: '15px', right: '15px', padding: '6px 16px', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
            {copiedInlineAll ? 'Copied!' : 'Copy'}
          </button>
        </div>
        <img src="/assets/wix-8.png" alt="Paste Code" style={{ maxWidth: '100%', borderRadius: '8px', border: '1px solid #ddd', margin: '15px 0' }} />


        <p style={{ fontSize: '15px', color: '#666', marginBottom: '10px', marginTop: '20px' }}>
          <strong>4. Adjust the widget's size and position</strong><br/>
          Drag the widget to the right page area and stretch it to get the size you require. Keep stretching until scroll bars are gone:
        </p>
        <img src="/assets/file-brabujqgLj.gif" alt="Paste Code" style={{ maxWidth: '100%', borderRadius: '8px', border: '1px solid #ddd', margin: '15px 0' }} />


        <p style={{ fontSize: '15px', color: '#666', marginBottom: '10px', marginTop: '20px' }}>
          <strong>5. Publish the widget to all pages</strong><br/>
          Right-click on the widget's container → select <strong>Pin to Screen</strong> → turn on <strong>Show on All Pages</strong> toggle and click <strong>Publish</strong>:
        </p>
        <img src="/assets/wix-9.png" alt="Paste Code" style={{ maxWidth: '100%', borderRadius: '8px', border: '1px solid #ddd', margin: '15px 0' }} />

      </div>

      <div style={{ marginBottom: '40px' }}>
        <h2 style={{ fontSize: '24px', marginBottom: '20px', color: '#222' }}>Adding a floating widget to a specific page/all pages</h2>
        
        <div style={{ background: '#fff3cd', borderLeft: '4px solid #ffecb5', padding: '10px', marginBottom: '20px', fontSize: '14px' }}>
          <strong>Note:</strong> The Custom Code feature in Wix is available only on <strong>Wix Premium plans</strong> with a connected <strong>custom domain</strong>.
        </div>

        <p style={{ fontSize: '15px', color: '#666', marginBottom: '10px' }}>
          <strong>1. Go to Wix website dashboard</strong><br/>
          Log in to your Wix account and choose the website where you'd like to add the widget. Click on the <strong>Settings</strong> section in your Dashboard menu, find the <strong>Advanced</strong> section and choose <strong>Custom code</strong>:
        </p>
        <img src="/assets/wix-10.png" alt="Paste Code" style={{ maxWidth: '100%', borderRadius: '8px', border: '1px solid #ddd', margin: '15px 0' }} />


        <p style={{ fontSize: '15px', color: '#666', marginBottom: '10px', marginTop: '20px' }}>
          <strong>2. Add Custom Code</strong><br/>
          Click on the + <strong>Add Custom Code</strong> button in the right upper corner:
        </p>
        <img src="/assets/wix-11.png" alt="Paste Code" style={{ maxWidth: '100%', borderRadius: '8px', border: '1px solid #ddd', margin: '15px 0' }} />


        <p style={{ fontSize: '15px', color: '#666', marginBottom: '10px', marginTop: '20px' }}>
          <strong>3. Insert the code</strong><br/>
          Insert the widget’s installation code into the <strong>Paste the code snippet here</strong> field. In the <strong>Place Code in</strong> options, select <strong>Body - start</strong>, then click <strong>Apply</strong> to save the changes.
        </p>

        <div style={{ position: 'relative', marginBottom: '20px' }}>
          <pre style={{ background: '#f4f4f4', padding: '20px', border: '1px solid #ddd', borderRadius: '5px', fontFamily: 'Courier New, monospace', fontSize: '14px', overflowX: 'auto' }}>
            {fullCode}
          </pre>
          <button onClick={() => handleCopy(fullCode, setCopiedFloatingAll)} className='btn-color' style={{ position: 'absolute', top: '15px', right: '15px', padding: '6px 16px', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 'bold' }}>
            {copiedFloatingAll ? 'Copied!' : 'Copy'}
          </button>
        </div>
        <img src="/assets/wix-12.png" alt="Paste Code" style={{ maxWidth: '100%', borderRadius: '8px', border: '1px solid #ddd', margin: '15px 0' }} />


        <p style={{ fontSize: '15px', color: '#666', marginBottom: '10px', marginTop: '20px' }}>
          If you want to add the widget to a specific page, click <strong>Choose specific pages</strong>, select the page from the dropdown, choose <strong>Body - start</strong> in the <strong>Place Code in</strong> options, and click <strong>Apply</strong>.
        </p>
        <img src="/assets/wix-13.png" alt="Paste Code" style={{ maxWidth: '100%', borderRadius: '8px', border: '1px solid #ddd', margin: '15px 0' }} />

      </div>

    </div>
  );
};

export default WixGuide;
